import { useEffect, useRef } from "react";
import type { MouseEvent, ReactNode } from "react";
import { Logo } from "@/components/Logo";
import { Icon, type IconName } from "@/components/Icon";
import { LogoutButton } from "@/components/LogoutButton";
import { SetupMenu } from "@/components/SetupMenu";
import { EntitySwitcher } from "@/components/EntitySwitcher";
import "./AppShell.css";

export type NavKey = "dashboard" | "books" | "payroll" | "farm" | "reports" | "setup";

interface NavItem {
  key: NavKey;
  path: string;
  /** Accessible name — the SAME word on both the laptop sidebar and the phone bottom
   *  nav (ADR 0009 §2: tabs, and the tiles above them, "render the same on laptop and
   *  phone"). Replaces the old `label`/`mobileLabel` split ("Dashboard" vs "Home"). */
  label: string;
  icon: IconName;
}

/**
 * The five tiles, in the reading order ADR 0009 §1 lists them, on both the laptop
 * sidebar and the phone bottom nav. `/entry` is deliberately absent — the QR-code
 * visitor journey stays a focused single-purpose screen with no way to wander off
 * mid-entry (see App.tsx). Setup is ALSO deliberately absent from this list: it is the
 * gear, rendered by `SetupMenu` outside this array (and outside the Primary nav)
 * entirely, so it never counts toward the measured 320px five-tile budget (ADR 0009 §1).
 *
 * Icon.tsx carries no farm-specific glyph and sits outside this job's file scope —
 * "sparkle" is a placeholder reuse, not a considered choice; a real Farm icon is a
 * follow-up.
 */
const NAV_ITEMS: NavItem[] = [
  { key: "dashboard", path: "/", label: "Home", icon: "grid" },
  { key: "books", path: "/books", label: "Books", icon: "card" },
  { key: "payroll", path: "/payroll", label: "Payroll", icon: "dollar" },
  { key: "farm", path: "/farm", label: "Farm", icon: "sparkle" },
  { key: "reports", path: "/reports", label: "Reports", icon: "chart" },
];

interface AppShellProps {
  active: NavKey;
  onNavigate: (path: string) => void;
  /** feature/auth: ends the session. Rendered here (desktop sidebar + phone
   *  topbar) rather than per-page, so every shell route gets it for free —
   *  Dashboard is the one exception, since it renders its own masthead
   *  instead of this component's topbar; see Dashboard.tsx. */
  onLogout: () => Promise<void>;
  children: ReactNode;
}

/**
 * The navigable app frame: a sidebar on laptop widths, a bottom tab bar on
 * phone widths, per `easyfarm-cloud-demo.html`. Deliberately quiet — ADR 0004
 * reserves the app's one decorative gesture (the horizon header) for the
 * Dashboard screen alone, so this chrome stays a plain white sidebar/tab bar,
 * exactly like the mock's `.sidebar`/`.bnav`.
 *
 * Renders neither route's content itself; App.tsx picks the page and passes
 * it in as `children`; this component owns only the chrome around it.
 */
export function AppShell({ active, onNavigate, onLogout, children }: AppShellProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Move focus to the top of whatever screen just replaced the old one, so a
    // keyboard/screen-reader user isn't left on a sidebar link that no longer
    // describes where they are. Skipped on mount — the browser's own initial
    // focus (or a hash target) should win on first load, not this effect.
    // ⚠️ THIS GUARD LOOKS BROKEN IN DEV AND IS NOT. React StrictMode mounts, unmounts and
    // re-mounts every effect, so the second mount consumes the ref and the focus below DOES
    // fire on load — against the Vite dev server the Playwright suite runs on. Measured
    // 2026-09-08 on the same page at the same moment: dev focuses #app-shell-content before
    // any Tab (so the first Tab reaches the business switcher and the nav is unreachable
    // going forward), while the BUILT bundle calls focus() zero times on load and the first
    // Tab reaches "Skip to content".
    //
    // So do not "fix" this from a dev-server observation: deleting the guard breaks the
    // shipped app, and no e2e test can tell you so. deploy/check-keyboard-nav.sh asserts it
    // against the artifact that ships, and its header carries both measurements.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    contentRef.current?.focus();
  }, [active]);

  function handleNavClick(e: MouseEvent<HTMLAnchorElement>, path: string): void {
    // Real <a> elements stay real links: a modified click (new tab, new window,
    // middle-click) or a right-click must keep working exactly like any other
    // link on the web. Only a plain left click is intercepted for client-side
    // navigation.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    onNavigate(path);
  }

  function renderLinks(variant: "sidebar" | "bottom") {
    return NAV_ITEMS.map((item) => {
      const isActive = item.key === active;
      return (
        <li key={item.key} className={`app-shell__nav-item app-shell__nav-item--${variant}`}>
          <a
            href={item.path}
            className={`app-shell__nav-link app-shell__nav-link--${variant}${
              isActive ? " app-shell__nav-link--active" : ""
            }`}
            // The one place color is NOT the only channel telling a sighted user
            // which screen they're on: aria-current carries it for everyone else,
            // and the active link is also bold (see AppShell.css).
            aria-current={isActive ? "page" : undefined}
            onClick={(e) => handleNavClick(e, item.path)}
          >
            <Icon name={item.icon} size={variant === "sidebar" ? 18 : 20} />
            <span>{item.label}</span>
          </a>
        </li>
      );
    });
  }

  return (
    <div className="app-shell">
      {/* Keyboard users get the same escape hatch every screen already needs:
          jump past the nav straight to the content without tabbing through it. */}
      <a
        className="visually-hidden"
        href="#app-shell-content"
        onClick={(e) => {
          e.preventDefault();
          contentRef.current?.focus();
        }}
      >
        Skip to content
      </a>

      <aside className="app-shell__sidebar">
        {/* Skipped on Dashboard: its own masthead carries a logo "at every width,
            mobile included" (see the topbar comment below), and the sidebar is NEVER
            hidden at laptop widths the way the topbar is — so on Home the two used to
            render at once, a pre-existing gap `shell.spec.ts`'s AC8 ("the logo appears
            exactly once") newly measures. Every other route has no masthead of its own,
            so this is the only one that needs to skip it. */}
        {active !== "dashboard" && (
          <div className="app-shell__brand">
            <Logo size={26} onDark />
          </div>
        )}
        <nav className="app-shell__nav" aria-label="Primary">
          <ul className="app-shell__nav-list">{renderLinks("sidebar")}</ul>
        </nav>
        <div className="app-shell__sidebar-footer">
          {/* Above Log out (ADR 0009 §1: "sidebar footer above Log out"), and OUTSIDE
              the `<nav>` above — a sibling <div>, not a list item — so it never counts
              toward the five-tile budget `shell.spec.ts` measures on `<nav
              aria-label="Primary">`. */}
          <SetupMenu active={active === "setup"} onNavigate={onNavigate} variant="sidebar" />
          <LogoutButton onLogout={onLogout} />
        </div>
      </aside>

      <div className="app-shell__column">
        {/* Dashboard renders its own full masthead (Logo + tagline + the "Live"
            pill, inside a <header> of its own — see Dashboard.tsx/.css) and
            that stays visible at every width, mobile included, because
            dashboard.spec.ts asserts on it there. A SECOND <header> here would
            put two banner landmarks (and two copies of the wordmark) on screen
            at once on a phone, so AppShell renders nothing at all for this
            route — not even a thin strip. The Setup gear a phone Home screen
            still needs (shell.spec.ts's AC1) now lives INSIDE Dashboard's own
            horizon row instead (Dashboard.tsx/.css) — one band, not two
            (shell.spec.ts's AC-c5). LogoutButton stays out of both: Dashboard
            already renders its own (`.dashboard__logout`). */}
        {active !== "dashboard" && (
          <header className="app-shell__topbar">
            <Logo size={24} onDark />
            <SetupMenu active={active === "setup"} onNavigate={onNavigate} variant="topbar" />
            <LogoutButton onLogout={onLogout} className="app-shell__topbar-logout" />
          </header>
        )}

        {/* ef-047-1 — the one entity switcher, visible at every width (unlike the
            phone-only `.app-shell__topbar` above): Fields/Grain/Livestock are the
            entity-scoped screens under Farm, so this bar renders only there rather
            than on every route the way the sidebar/topbar chrome does. */}
        {active === "farm" && (
          <div className="app-shell__entity-bar">
            <EntitySwitcher variant="light" />
          </div>
        )}

        {/* Not a <main> landmark itself — each page (Dashboard, Payroll, Reports,
            Capture) owns exactly one <main> of its own, same as the shell-less
            QuickEntry screen. This div is a focus-management container only:
            tabIndex=-1 makes it a valid programmatic-focus target without adding
            a second landmark for assistive tech to wade through. */}
        <div id="app-shell-content" className="app-shell__content" ref={contentRef} tabIndex={-1}>
          {children}
        </div>
      </div>

      <nav className="app-shell__bottom-nav" aria-label="Primary">
        <ul className="app-shell__nav-list app-shell__nav-list--bottom">
          {renderLinks("bottom")}
        </ul>
      </nav>
    </div>
  );
}
